import { useEffect, useState } from 'react';
import { addLeadProduct, type KleedVorm } from '../../lib/lead-product';
import {
  OFFERTE_PRIMARY_PRODUCTS,
  DEFAULT_OFFERTE_PRIMARY_SKU,
  findOffertePrimaryProduct,
} from '../../lib/offerte-products';

interface AddKleedDialogProps {
  isOpen: boolean;
  leadId: string;
  onClose: () => void;
  onAdded: () => void;
}

const VORM_OPTIONS: { value: KleedVorm; label: string }[] = [
  { value: 'rechthoekig', label: 'Rechthoekig' },
  { value: 'rond', label: 'Rond' },
  { value: 'overig', label: 'Overig' },
];

export function AddKleedDialog({ isOpen, leadId, onClose, onAdded }: AddKleedDialogProps) {
  const [productCode, setProductCode] = useState<string>(DEFAULT_OFFERTE_PRIMARY_SKU);
  const [vorm, setVorm] = useState<KleedVorm>('rechthoekig');
  const [lengte, setLengte] = useState('');
  const [breedte, setBreedte] = useState('');
  const [diameter, setDiameter] = useState('');
  const [oppervlakte, setOppervlakte] = useState('');
  const [aantal, setAantal] = useState('1');
  const [bijzonderheden, setBijzonderheden] = useState('');
  const [externeReferentie, setExterneReferentie] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setProductCode(DEFAULT_OFFERTE_PRIMARY_SKU);
    setVorm('rechthoekig');
    setLengte('');
    setBreedte('');
    setDiameter('');
    setOppervlakte('');
    setAantal('1');
    setBijzonderheden('');
    setExterneReferentie('');
    setError(null);
  }, [isOpen]);

  if (!isOpen) return null;

  const product = findOffertePrimaryProduct(productCode);
  const isCountBased = product.mode === 'count';

  const valid = (() => {
    if (isCountBased) return Number(aantal) > 0;
    if (vorm === 'rechthoekig') return Number(lengte) > 0 && Number(breedte) > 0;
    if (vorm === 'rond') return Number(diameter) > 0;
    return Number(oppervlakte) > 0;
  })();

  async function handleSubmit() {
    setSaving(true);
    setError(null);
    try {
      const res = await addLeadProduct({
        leadId,
        productCode,
        vorm: isCountBased ? undefined : vorm,
        lengteCm: !isCountBased && vorm === 'rechthoekig' ? Number(lengte) : null,
        breedteCm: !isCountBased && vorm === 'rechthoekig' ? Number(breedte) : null,
        diameterCm: !isCountBased && vorm === 'rond' ? Number(diameter) : null,
        oppervlakteM2: !isCountBased && vorm === 'overig' ? Number(oppervlakte) : null,
        aantal: isCountBased ? Number(aantal) : null,
        bijzonderheden: bijzonderheden || null,
        externeReferentie: externeReferentie.trim() || null,
      });
      if (!res.success) {
        setError(res.message || 'Toevoegen mislukt');
        return;
      }
      onAdded();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Toevoegen mislukt');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <h3 className="text-base font-semibold text-slate-900">Kleed toevoegen</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
            aria-label="Sluiten"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <label className="block">
            <span className="text-xs text-slate-600">Product</span>
            <select
              value={productCode}
              onChange={(e) => setProductCode(e.target.value)}
              className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
            >
              {OFFERTE_PRIMARY_PRODUCTS.map((p) => (
                <option key={p.sku} value={p.sku}>
                  {p.label}
                </option>
              ))}
            </select>
            {product.hint && (
              <span className="mt-1 block text-[11px] text-slate-500">{product.hint}</span>
            )}
          </label>

          {isCountBased ? (
            <label className="block">
              <span className="text-xs text-slate-600">Aantal (stuks)</span>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                value={aantal}
                onChange={(e) => setAantal(e.target.value)}
                className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
              />
            </label>
          ) : (
            <>
              <div>
                <span className="text-xs text-slate-600">Vorm</span>
                <div className="mt-1 inline-flex rounded-md border border-slate-300 overflow-hidden">
                  {VORM_OPTIONS.map((opt, i) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setVorm(opt.value)}
                      className={
                        'px-3 py-1.5 text-sm ' +
                        (vorm === opt.value
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white text-slate-700 hover:bg-slate-50') +
                        (i > 0 ? ' border-l border-slate-300' : '')
                      }
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {vorm === 'rechthoekig' && (
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-xs text-slate-600">Lengte (cm)</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      value={lengte}
                      onChange={(e) => setLengte(e.target.value)}
                      className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-slate-600">Breedte (cm)</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      value={breedte}
                      onChange={(e) => setBreedte(e.target.value)}
                      className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                    />
                  </label>
                </div>
              )}

              {vorm === 'rond' && (
                <label className="block">
                  <span className="text-xs text-slate-600">Diameter (cm)</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={diameter}
                    onChange={(e) => setDiameter(e.target.value)}
                    className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  />
                </label>
              )}

              {vorm === 'overig' && (
                <label className="block">
                  <span className="text-xs text-slate-600">Oppervlakte (m²)</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0.01}
                    step={0.01}
                    value={oppervlakte}
                    onChange={(e) => setOppervlakte(e.target.value)}
                    className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  />
                </label>
              )}
            </>
          )}

          <label className="block">
            <span className="text-xs text-slate-600">Bijzonderheden (optioneel)</span>
            <textarea
              rows={3}
              maxLength={1000}
              value={bijzonderheden}
              onChange={(e) => setBijzonderheden(e.target.value)}
              placeholder="Bijv. randen iets gerafeld, vlek bij het midden"
              className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
            />
          </label>

          <label className="block">
            <span className="text-xs text-slate-600">Externe referentie (optioneel)</span>
            <input
              type="text"
              maxLength={80}
              value={externeReferentie}
              onChange={(e) => setExterneReferentie(e.target.value)}
              placeholder="Bijv. museum-objectnr 002.523, claimnummer"
              className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
            />
          </label>

          <p className="text-xs text-slate-400">
            {product.hasPackages
              ? 'Als de offerte al een pakket binnen dezelfde productfamilie heeft, wordt dat automatisch op het nieuwe kleed toegepast.'
              : 'Dit product heeft geen pakket-keuze.'}
          </p>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || !valid}
            className="text-sm px-3 py-1.5 rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:bg-slate-300"
          >
            {saving ? 'Toevoegen…' : 'Toevoegen'}
          </button>
        </div>
      </div>
    </div>
  );
}
